import { useEffect, useRef, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useMatchStore } from '@/stores/matchStore';
import { useRoomStore } from '@/stores/roomStore';
import { useToasts } from '@/stores/toastStore';
import { useNet } from '@/net/useNet';
import { Avatar, RankMedal } from '@/art';
import { isImageAssetUrl, resolveAssetUrl } from '@/shop/assetUrl';
import type { FrameSpec, RankTier } from '@shared/types';
import { EV, type EquippedCosmetic, type MatchCountdown, type MatchFound, type TeamMatchFound, type TeamMember, type RoomSlotDto } from '@shared/protocol';
import { audio } from '@/audio/AudioService';
import { TipTicker } from '@/match/TipTicker';
import { useT } from '@/i18n/useI18n';

const DIVISION_ROMAN = ['', 'I', 'II', 'III', 'IV'] as const;

/** Build an Avatar frame spec from an equipped avatarFrame cosmetic (border/
 * glow CSS or an image overlay), mirroring LaneHeader. */
function frameFromEquipped(eq: EquippedCosmetic | null): FrameSpec | null {
  const d = eq?.data as Record<string, unknown> | undefined;
  if (!d) return null;
  return {
    border: (d.border as string | undefined) ?? null,
    glow: (d.glow as string | undefined) ?? null,
    imageUrl: (d.imageUrl as string | undefined) ?? null,
  };
}

/** Common card model so the duel layout can render players sourced from either
 * the room lobby (during search) or the team-found payload (after reveal). */
interface CardModel {
  id: string;
  name: string;
  mmr: number;
  rank: RankTier;
  division: number;
  avatarUrl: string | null;
  characterUrl: string | null;
  frame: FrameSpec | null;
  clanTag: string | null;
  clanEmblem: string | null;
}

function cardFromRoomUser(u: NonNullable<RoomSlotDto['user']>): CardModel {
  return {
    id: u.id,
    name: u.displayName,
    mmr: u.mmr,
    rank: u.rank as RankTier,
    division: u.rankDivision,
    avatarUrl: u.avatarUrl,
    characterUrl: u.characterIdleUrl ?? null,
    frame: u.frame ? { border: u.frame.border ?? null, glow: u.frame.glow ?? null, imageUrl: null } : null,
    clanTag: null,
    clanEmblem: null,
  };
}

function cardFromTeamMember(m: TeamMember): CardModel {
  return {
    id: m.id,
    name: m.name,
    mmr: m.mmr,
    rank: m.rank,
    division: 4,
    avatarUrl: m.avatarUrl ?? null,
    characterUrl: m.characterIdleUrl ?? null,
    frame: frameFromEquipped(m.equipped?.avatarFrame ?? null),
    clanTag: m.clanTag ?? null,
    clanEmblem: m.clanEmblem ?? null,
  };
}

type Phase = 'search' | 'reveal' | 'countdown';

const FALLBACK_OFFLINE_MS = 3500;
const REVEAL_TO_COUNTDOWN_MS = 1100;

export function MatchmakingScreen() {
  const go = useUIStore((s) => s.go);
  const t = useT();
  const profile = useUserStore((s) => s.profile);
  const startLocalMatch = useMatchStore((s) => s.startLocalMatch);
  const applyMatchFound = useMatchStore((s) => s.applyMatchFound);
  const applyTeamFound = useMatchStore((s) => s.applyTeamFound);
  const rivalNameStore = useMatchStore((s) => s.rivalName);
  const rivalMmrStore = useMatchStore((s) => s.rivalMmr);
  // Team (2v2) awareness — drives the multi-avatar layout. `teamPending` is set
  // at lobby-start before the match is found; `isTeam`/`yourTeam`/`rivalTeam`
  // populate once TEAM_MATCH_FOUND lands.
  const room = useRoomStore((s) => s.room);
  const teamPending = useMatchStore((s) => s.teamPending);
  const isTeam = useMatchStore((s) => s.isTeam);
  const yourTeam = useMatchStore((s) => s.yourTeam);
  const rivalTeam = useMatchStore((s) => s.rivalTeam);
  const { status, socket } = useNet({ autoConnect: true, failAfterMs: FALLBACK_OFFLINE_MS });

  const [phase, setPhase] = useState<Phase>('search');
  const [count, setCount] = useState<number | 'GO'>(3);
  const [rivalName, setRivalName] = useState(rivalNameStore);
  const [rivalMmr, setRivalMmr] = useState(rivalMmrStore);
  const [rivalRank, setRivalRank] = useState<RankTier>('bronze');
  const [rivalDivision, setRivalDivision] = useState(4);
  const [rivalAvatarUrl, setRivalAvatarUrl] = useState<string | null>(null);
  const [rivalCharacterUrl, setRivalCharacterUrl] = useState<string | null>(null);
  const [rivalFrame, setRivalFrame] = useState<FrameSpec | null>(null);
  const [rivalClanTag, setRivalClanTag] = useState<string | null>(null);
  const [rivalClanEmblem, setRivalClanEmblem] = useState<string | null>(null);
  const yourClanTag = useMatchStore((s) => s.yourClanTag);
  const yourClanEmblem = useMatchStore((s) => s.yourClanEmblem);

  const phaseRef = useRef<Phase>('search');
  phaseRef.current = phase;
  const fellBackRef = useRef(false);
  const matchFoundRef = useRef(false);

  // Drive the local-AI fallback flow when the server is unreachable. Mirrors
  // the original setTimeout machine so the screen still works offline.
  const runOfflineFlow = () => {
    if (fellBackRef.current) return;
    fellBackRef.current = true;
    setPhase('reveal');
    const seed = Date.now();
    startLocalMatch({ seed });
    const t1 = window.setTimeout(() => setPhase('countdown'), 900);
    const t2 = window.setTimeout(() => setCount(2), 1700);
    const t3 = window.setTimeout(() => setCount(1), 2700);
    const t4 = window.setTimeout(() => setCount('GO'), 3700);
    const t5 = window.setTimeout(() => go('match'), 4500);
    (runOfflineFlow as any).timers = [t1, t2, t3, t4, t5];
  };

  // ── Socket-driven happy path ─────────────────────────────────────
  useEffect(() => {
    if (status !== 'connected') return;
    if (fellBackRef.current) return;
    const timers: number[] = [];

    // MATCH_FOUND only contains the data needed to build the wall. Move to the
    // match screen first so Pixi can initialize there; MatchScreen emits
    // MATCH_READY only after the scene is actually renderable.
    const openPreparingMatch = () => {
      timers.push(window.setTimeout(() => go('match'), 700));
    };

    const onSearching = () => {
      if (phaseRef.current === 'search') setPhase('search');
    };
    const onFound = (payload: MatchFound) => {
      matchFoundRef.current = true;
      audio.play('match.found');
      applyMatchFound(payload);
      setRivalName(payload.rival.name);
      setRivalClanTag(payload.rival.clanTag ?? null);
      setRivalClanEmblem(payload.rival.clanEmblem ?? null);
      setRivalMmr(payload.rival.mmr);
      setRivalRank(payload.rival.rank);
      // Server doesn't send division yet; default to lowest of the tier.
      setRivalDivision(4);
      setRivalAvatarUrl(payload.rival.avatarUrl ?? null);
      setRivalCharacterUrl(payload.rival.characterIdleUrl ?? null);
      setRivalFrame(frameFromEquipped(payload.rival.equipped?.avatarFrame ?? null));
      // Brief delay before showing the reveal so the search→reveal transition
      // animates instead of jump-cutting when the match comes back fast.
      timers.push(window.setTimeout(() => setPhase('reveal'), 250));
      openPreparingMatch();
    };
    const onTeamFound = (payload: TeamMatchFound) => {
      matchFoundRef.current = true;
      audio.play('match.found');
      applyTeamFound(payload, profile.id);
      // Rival reveal pulls from the joined rival-team data the store computed.
      const st = useMatchStore.getState();
      const rival0 = payload.rivalTeam[0];
      setRivalName(st.rivalName);
      setRivalMmr(st.rivalMmr);
      setRivalRank(rival0?.rank ?? 'bronze');
      setRivalDivision(4);
      setRivalAvatarUrl(rival0?.avatarUrl ?? null);
      setRivalCharacterUrl(rival0?.characterIdleUrl ?? null);
      setRivalFrame(frameFromEquipped(rival0?.equipped?.avatarFrame ?? null));
      timers.push(window.setTimeout(() => setPhase('reveal'), 250));
      openPreparingMatch();
    };
    const onCountdown = ({ at }: MatchCountdown) => {
      setPhase('countdown');
      useMatchStore.getState().setMatchmakingPhase('countdown');
      if (at === 0) {
        setCount('GO');
        useMatchStore.getState().setCountdownAt('GO');
        audio.play('match.go');
      } else {
        setCount(at);
        useMatchStore.getState().setCountdownAt(at);
        audio.play('match.countdown');
      }
    };
    const onStart = () => {
      // Anchor the race clock to the SHARED MATCH_START moment (the server
      // broadcasts it to both players at once, after the ready-barrier) instead
      // of each client's own mount time — so both timers read the same elapsed.
      useMatchStore.setState({
        matchmakingPhase: 'racing',
        countdownAt: null,
        raceStartMs: Date.now(),
      });
      // The match clock starts NOW on the server; navigate to the live screen.
      go('match');
    };
    const onError = (err: { code: string; message: string }) => {
      console.warn('[matchmaking] server error', err);
      // Team matches require the server (no local-AI fallback). Bail home so we
      // don't drop the player into a solo offline match in the wrong mode.
      if (useMatchStore.getState().teamPending) { go('home'); return; }
      runOfflineFlow();
    };

    socket.on(EV.QUEUE_SEARCHING, onSearching);
    socket.on(EV.MATCH_FOUND, onFound);
    socket.on(EV.TEAM_MATCH_FOUND, onTeamFound);
    socket.on(EV.MATCH_COUNTDOWN, onCountdown);
    socket.on(EV.MATCH_START, onStart);
    socket.on(EV.ERROR, onError);

    // Room-initiated matches: the server already spawned the match before we
    // got here (via room:matchStart → RoomNotifier set source='room'). Skip
    // QUEUE_JOIN to avoid double-registering this socket as both queued AND
    // in-match. MATCH_FOUND will arrive on its own from the orchestrator.
    const src = useMatchStore.getState().matchSource;
    // War keys off the durable `warId`, NOT `matchSource`: a dependency-driven
    // cleanup→re-run of this effect can null out `matchSource` mid-search, which
    // previously dropped a war attack into the solo QUEUE_JOIN below (→ a regular
    // ranked match on rematch). `warId` survives that churn.
    const isWar = useMatchStore.getState().warId != null;
    // Clan war: the war-queue join was already emitted from the WarBoard; if no
    // rival shows up the server sends WAR_NO_OPPONENT → bail back to the clan.
    const onWarNoOpponent = (p: { message: string }) => {
      matchFoundRef.current = true; // suppress the cancel-on-cleanup below
      // Drop the war markers so the next Quick Match isn't stranded as a war.
      useMatchStore.setState({ matchSource: null, warId: null });
      useToasts.getState().push({ kind: 'warn', title: t('clan.war.title_toast'), body: p.message });
      go('clan');
    };
    socket.on(EV.WAR_NO_OPPONENT, onWarNoOpponent);

    if (src === 'room' || isWar) {
      // No-op — room match is already spawned; war already enqueued elsewhere.
    } else {
      useMatchStore.getState().setMatchSource('queue');
      socket.emit(EV.QUEUE_JOIN, { mode: 'solo', mmr: profile.mmr });
    }

    return () => {
      socket.off(EV.QUEUE_SEARCHING, onSearching);
      socket.off(EV.MATCH_FOUND, onFound);
      socket.off(EV.TEAM_MATCH_FOUND, onTeamFound);
      socket.off(EV.MATCH_COUNTDOWN, onCountdown);
      socket.off(EV.MATCH_START, onStart);
      socket.off(EV.ERROR, onError);
      timers.forEach((timer) => window.clearTimeout(timer));
      socket.off(EV.WAR_NO_OPPONENT, onWarNoOpponent);
      // If we leave matchmaking without entering a match, tell the server and
      // clear the source so a later Quick Match isn't stranded by a stale 'war'.
      if (!matchFoundRef.current) {
        socket.emit(isWar ? EV.WAR_QUEUE_CANCEL : EV.QUEUE_CANCEL, {});
        if (isWar) useMatchStore.setState({ matchSource: null, warId: null });
      }
    };
  }, [status, socket, profile.mmr, profile.id, applyMatchFound, applyTeamFound, go]);

  // ── Fallback after socket fails to connect ───────────────────────
  useEffect(() => {
    if (status !== 'failed') return;
    // No offline AI for 2v2 — if the connection failed, return home.
    if (useMatchStore.getState().teamPending) { go('home'); return; }
    runOfflineFlow();
    return () => {
      const timers = (runOfflineFlow as any).timers as number[] | undefined;
      timers?.forEach((t) => window.clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // ── Heuristic reveal nudge: if we get a match.found but no countdown
  // arrives within REVEAL_TO_COUNTDOWN_MS, the server is fine but slow —
  // surface SOMETHING so the screen doesn't feel frozen.
  useEffect(() => {
    if (phase !== 'reveal') return;
    const t = window.setTimeout(() => {
      if (phaseRef.current === 'reveal') {
        // Stay on reveal; server's countdown will kick us forward.
      }
    }, REVEAL_TO_COUNTDOWN_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  // ── 2v2 layout data ──────────────────────────────────────────────
  const teamMatch = !!teamPending || isTeam;
  // My side in the room — needed to split the lobby slots into "my team" vs
  // "rival team" while the team-found payload hasn't arrived yet.
  const mySide = room?.slots.find((s) => s.user?.id === profile.id)?.side ?? null;
  const myCards: CardModel[] = teamMatch
    ? isTeam && yourTeam.length
      ? yourTeam.map(cardFromTeamMember)
      : room
        ? room.slots.filter((s) => s.user && s.side === mySide).map((s) => cardFromRoomUser(s.user!))
        : []
    : [];
  const rivalCards: CardModel[] = teamMatch && isTeam && rivalTeam.length
    ? rivalTeam.map(cardFromTeamMember)
    : [];
  // How many rival placeholders to show while searching (mirror my team size).
  const rivalSlots = Math.max(myCards.length || 2, rivalCards.length);

  return (
    <div
      className="screen screen-fit screen-fit--dense scanlines matchmaking-screen"
      style={{
        background:
          'radial-gradient(ellipse at center, var(--bg-mid) 0%, var(--bg-deep) 70%)',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className="hex-bg" />

      <div className="matchmaking-header" style={{ position: 'absolute', top: 60, width: '100%', textAlign: 'center' }}>
        <div className="tag" style={{ color: 'var(--c-yellow)' }}>
          {phase === 'search' ? t('mm.searching_tag') : phase === 'reveal' ? t('mm.found_tag') : t('mm.ready_tag')}
        </div>
        <div className="display" style={{ fontSize: 28, marginTop: 8 }}>
          {phase === 'search'
            ? t('mm.searching_title')
            : phase === 'reveal'
              ? t('mm.found_title')
              : t('mm.ready_title')}
        </div>
        {status === 'failed' && (
          <div
            style={{
              marginTop: 8,
              fontSize: 10,
              color: 'var(--ink-dim)',
              fontFamily: 'var(--f-mono)',
              letterSpacing: '0.08em',
            }}
          >
            {t('mm.offline')}
          </div>
        )}
      </div>

      {teamMatch ? (
        <div
          className="matchmaking-duel"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            transform: phase === 'search' ? 'scale(0.92)' : 'scale(1)',
            transition: 'transform 0.6s ease',
            position: 'relative',
          }}
        >
          {/* My team — always revealed (I know my own teammates from the lobby). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {myCards.map((c) => (
              <DuelistCard
                key={c.id}
                color="red"
                name={c.name}
                mmr={c.mmr}
                rank={c.rank}
                division={c.division}
                avatarUrl={c.avatarUrl}
                characterUrl={c.characterUrl}
                frame={c.frame}
                clanTag={c.clanTag}
                clanEmblem={c.clanEmblem}
                side="L"
                reveal
                compact
              />
            ))}
          </div>

          <VsBadge phase={phase} />

          {/* Rival team — placeholder shimmer until TEAM_MATCH_FOUND lands. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {phase === 'search'
              ? Array.from({ length: rivalSlots }).map((_, i) => (
                  <DuelistCard
                    key={`ph-${i}`}
                    color="blue"
                    name=""
                    mmr={0}
                    rank="bronze"
                    division={4}
                    avatarUrl={null}
                    side="R"
                    reveal={false}
                    searching
                    compact
                  />
                ))
              : rivalCards.map((c) => (
                  <DuelistCard
                    key={c.id}
                    color="blue"
                    name={c.name}
                    mmr={c.mmr}
                    rank={c.rank}
                    division={c.division}
                    avatarUrl={c.avatarUrl}
                    characterUrl={c.characterUrl}
                    frame={c.frame}
                    clanTag={c.clanTag}
                    clanEmblem={c.clanEmblem}
                    side="R"
                    reveal
                    compact
                  />
                ))}
          </div>
        </div>
      ) : (
        <div
          className="matchmaking-duel"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            transform: phase === 'search' ? 'scale(0.92)' : 'scale(1)',
            transition: 'transform 0.6s ease',
            position: 'relative',
          }}
        >
          <DuelistCard
            color="red"
            name={profile.name}
            mmr={profile.mmr}
            rank={profile.rank}
            division={profile.rankDivision}
            avatarUrl={profile.avatarUrl}
            characterUrl={profile.characterIdleUrl}
            frame={profile.frame}
            clanTag={yourClanTag}
            clanEmblem={yourClanEmblem}
            side="L"
            reveal
          />

          <VsBadge phase={phase} />

          <DuelistCard
            color="blue"
            name={rivalName}
            mmr={rivalMmr}
            rank={rivalRank}
            division={rivalDivision}
            avatarUrl={rivalAvatarUrl}
            characterUrl={rivalCharacterUrl}
            frame={rivalFrame}
            clanTag={rivalClanTag}
            clanEmblem={rivalClanEmblem}
            side="R"
            reveal={phase !== 'search'}
            searching={phase === 'search'}
          />
        </div>
      )}

      {phase === 'countdown' && (
        <div
          className="countdown-digit"
          key={String(count)}
          style={{ fontSize: count === 'GO' ? 120 : 200 }}
        >
          {count}
        </div>
      )}

      <div className="matchmaking-footer" style={{ position: 'absolute', bottom: 40, left: 18, right: 18 }}>
        <div className="matchmaking-footer-extras">
          <TipTicker style={{ marginBottom: 12 }} />
          <div className="card" style={{ padding: 12, marginBottom: 12 }}>
            <div className="tag" style={{ marginBottom: 6 }}>
              {t('mm.stage')}
            </div>
            <div className="display" style={{ fontSize: 16 }}>
              {t('mm.stage_name')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 4 }}>
              {t('mm.stage_desc')}
            </div>
          </div>
        </div>
        {/* Cancel — only available before the rival is revealed. Once the
            duel is incoming we don't show the button because the orchestrator
            has already spawned the match server-side. */}
        {phase === 'search' && (
          <button
            onClick={() => {
              const isWar = useMatchStore.getState().warId != null;
              try {
                if (socket?.connected) socket.emit(isWar ? EV.WAR_QUEUE_CANCEL : EV.QUEUE_CANCEL, {});
              } catch { /* */ }
              // Clear any pending 2v2 flag so a stale teamPending doesn't bail
              // a later solo search home. Drop war markers too so the next Quick
              // Match isn't treated as a war.
              useMatchStore.setState({ teamPending: null, matchSource: null, warId: null });
              // War searches return to the clan screen; lobby searches return to
              // the room; otherwise home.
              go(isWar ? 'clan' : useRoomStore.getState().room ? 'room' : 'home');
            }}
            className="btn btn-ghost"
            style={{
              width: '100%',
              padding: '12px',
              fontSize: 13,
              color: 'var(--c-red)',
              borderColor: 'rgba(230,57,70,0.4)',
              background: 'rgba(230,57,70,0.06)',
            }}
          >
            {t('mm.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}

interface VsBadgeProps {
  phase: Phase;
}

function VsBadge({ phase }: VsBadgeProps) {
  return (
    <div
      style={{
        position: 'relative',
        width: 80,
        height: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Radar pulses during search */}
      {phase === 'search' && (
        <>
          <PulseRing delay={0} />
          <PulseRing delay={0.5} />
          <PulseRing delay={1.0} />
        </>
      )}
      <div
        className="display"
        style={{
          fontSize: phase === 'search' ? 36 : 56,
          color: phase === 'search' ? 'var(--c-cyan)' : 'var(--c-yellow)',
          textShadow:
            phase === 'search'
              ? '0 0 16px rgba(34,211,238,0.6)'
              : '0 0 24px rgba(244,199,36,0.8), 0 6px 0 rgba(0,0,0,0.6)',
          transform:
            phase === 'reveal'
              ? 'scale(1.15) rotate(0)'
              : phase === 'search'
                ? 'scale(0.8) rotate(-8deg)'
                : 'scale(1) rotate(0)',
          transition: 'all 0.5s cubic-bezier(0.34,1.56,0.64,1)',
          animation: phase === 'reveal' ? 'count-pop 0.5s ease-out' : undefined,
        }}
      >
        VS
      </div>
    </div>
  );
}

interface DuelistCardProps {
  color: 'red' | 'blue';
  name: string;
  mmr: number;
  rank: RankTier;
  division: number;
  avatarUrl: string | null;
  characterUrl?: string | null;
  frame?: FrameSpec | null;
  side: 'L' | 'R';
  reveal: boolean;
  /** True for the rival card while the queue is still searching — shows a
   * placeholder shimmer instead of a real card. */
  searching?: boolean;
  /** Tighter sizing for the 2v2 layout (two cards stacked per side). */
  compact?: boolean;
  clanTag?: string | null;
  clanEmblem?: string | null;
}

function DuelistCard({
  color,
  name,
  mmr,
  rank,
  division,
  avatarUrl,
  characterUrl,
  frame,
  side,
  reveal,
  searching,
  compact,
  clanTag,
  clanEmblem,
}: DuelistCardProps) {
  const t = useT();
  const rim = color === 'red' ? 'var(--c-red)' : 'var(--c-blue)';
  const glow =
    color === 'red'
      ? '0 0 24px rgba(230,57,70,0.55)'
      : '0 0 24px rgba(59,130,246,0.55)';
  const avatarSize = compact ? 46 : 64;
  return (
    <div
      style={{
        width: compact ? 96 : 110,
        padding: compact ? '7px 6px 9px' : '10px 8px 12px',
        background: searching
          ? 'rgba(0,0,0,0.6)'
          : 'rgba(0,0,0,0.4)',
        border: `2px solid ${searching ? 'var(--hairline)' : rim}`,
        borderRadius: 14,
        boxShadow: searching ? 'none' : glow,
        transform: reveal
          ? 'translateX(0)'
          : `translateX(${side === 'L' ? '-30px' : '30px'})`,
        opacity: reveal ? 1 : searching ? 0.55 : 0,
        transition:
          'transform 0.45s cubic-bezier(0.34,1.56,0.64,1), opacity 0.45s, border-color 0.4s, box-shadow 0.4s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {searching && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.07) 50%, transparent 70%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s linear infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {searching ? (
        <div
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
            border: '2px dashed var(--ink-dim)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-dim)',
            fontFamily: 'var(--f-display)',
            fontSize: 11,
          }}
        >
          ?
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Avatar color={color} size={avatarSize} photoUrl={avatarUrl} characterUrl={characterUrl} frame={frame} ring />
          <div
            style={{
              position: 'absolute',
              bottom: -8,
              right: -8,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
            }}
          >
            <RankMedal rank={rank} size={28} />
          </div>
        </div>
      )}

      <div
        className="display"
        style={{
          fontSize: 12,
          marginTop: searching ? 0 : 4,
          letterSpacing: '0.04em',
          color: searching ? 'var(--ink-dim)' : 'var(--ink)',
        }}
      >
        {searching ? t('mm.scanning') : name}
      </div>
      {!searching && clanTag && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
          {isImageAssetUrl(clanEmblem)
            ? <img src={resolveAssetUrl(clanEmblem) ?? ''} alt="" style={{ width: 14, height: 14, objectFit: 'contain' }} />
            : <span style={{ fontSize: 12, lineHeight: 1 }}>{clanEmblem || '🛡️'}</span>}
          <span className="mono" style={{ fontSize: 9, color: 'var(--c-yellow)', letterSpacing: '0.04em' }}>[{clanTag}]</span>
        </div>
      )}
      {!searching && (
        <div
          className="mono"
          style={{
            fontSize: 9,
            color: rim,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {rank} {DIVISION_ROMAN[division] ?? 'IV'}
        </div>
      )}
      <div
        className="mono"
        style={{ fontSize: 10, color: 'var(--ink-muted)' }}
      >
        {searching ? '— MMR' : `${mmr} MMR`}
      </div>
    </div>
  );
}

function PulseRing({ delay }: { delay: number }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 70,
        height: 70,
        marginLeft: -35,
        marginTop: -35,
        borderRadius: '50%',
        border: '2px solid var(--c-cyan)',
        opacity: 0.7,
        animation: 'radar-pulse 1.6s ease-out infinite',
        animationDelay: `${delay}s`,
        pointerEvents: 'none',
      }}
    />
  );
}
